import React, { useState } from 'react';
import axios from 'axios';
import {
  Flex,
  Box,
  Row,
  Column,
  Card,
  Button,
} from '../../../../design-system';
import dayjs from '../../../../helpers/dates';
import styled from 'styled-components';
import { Form, Field } from 'react-final-form';
import { AutosaveEffect } from '../../../../hooks/useAutosave';
import useSaveAction from '../../../../hooks/useSaveAction';
import {
  prettyPhone,
  currencyFormatter,
  formatEIN,
  isExpired,
} from '../../../../helpers/utils';
import { successRefetch } from '../../../../helpers/data-loader';
import { ExpiredCard } from '../../../../components/Expired';
import FieldColumn from '../../../../components/FieldColumn';
import FieldText from '../../../../components/FieldText';
import CardHeader from '../../../../components/CardHeader';
import ClientLogo from '../../../../components/ClientLogo';
import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS,
  US_STATES,
} from '../../../../globalConstants';
import GENERAL_INFO_FIELDS from './constants';
import DateField from '../../../../components/DateField';

const Image = styled.img`
  width: 150px;
  height: auto;
`;

interface GeneralInformationProps {
  data: any;
  refetch: () => void;
  canEdit: boolean;
}

const GeneralInformation = ({
  data,
  refetch,
  canEdit,
}: GeneralInformationProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [lastSave, setLastSave] = useState<any>(undefined);
  const [isLogoModalOpen, setLogoModalOpen] = useState(false);
  const {
    id,
    phone,
    fax,
    current_budget,
    tax_id,
    logo,
    updated_at: updatedAt,
  } = data;

  const [onSubmit, saving] = useSaveAction(async (values: any) => {
    await axios.put(`/client/${id}`, {
      ...values,
      current_budget:
        values.current_budget && values.current_budget.replace(/[^0-9.]/g, ''),
    });
    setLastSave(dayjs());
  });

  const toggleLogoModal = () => {
    setLogoModalOpen(!isLogoModalOpen);
  };

  const finishEditing = () => {
    setIsEditing(false);
    successRefetch(refetch);
  };

  return (
    <>
      <ExpiredCard expired={isExpired(data.expires)}>
        <CardHeader
          title="General Information"
          updatedAt={lastSave || updatedAt}
          saving={saving}
        />
        <Card.Body>
          {isEditing ? (
            <>
              <Row>
                <Column p={3}>
                  <ClientLogo
                    isOpen={isLogoModalOpen}
                    onClick={toggleLogoModal}
                    onClose={toggleLogoModal}
                    canEdit={canEdit}
                    clientId={id}
                    refetch={refetch}
                    logo={logo}
                  />
                </Column>
              </Row>
              <Flex>
                <Form
                  onSubmit={onSubmit}
                  keepDirtyOnReinitialize
                  initialValues={{
                    country: 'United States',
                    ...data,
                    state:
                      US_STATES.find(
                        ({ abbreviation }) => abbreviation === data.state,
                      )?.name || data.state,
                    phone: phone && prettyPhone(phone),
                    fax: fax && prettyPhone(fax),
                    current_budget:
                      current_budget &&
                      currencyFormatter.format(current_budget),
                    tax_id: tax_id && formatEIN(tax_id),
                  }}
                  render={({ handleSubmit, values, form }) => (
                    <form onSubmit={handleSubmit}>
                      <Row mb={25}>
                        {GENERAL_INFO_FIELDS.map((field) => {
                          if (field.type === 'date') {
                            return (
                              <Column key={field.name} pb={2} width={300}>
                                <Field name={field.name}>
                                  {({ input }) => {
                                    return (
                                      <DateField
                                        name={field.name}
                                        label={field.label}
                                        selected={input.value}
                                        onChange={(value) =>
                                          input.onChange(value)
                                        }
                                        showPastDue={false}
                                      />
                                    );
                                  }}
                                </Field>
                              </Column>
                            );
                          }

                          return field.name === 'empty' ? (
                            <Column
                              key={field.name}
                              mt={2}
                              pb={3}
                              width={250}
                            />
                          ) : (
                            <FieldColumn
                              key={field.name}
                              width={250}
                              {...field}
                              {...(field.name === 'state' && {
                                options: values.country
                                  ? STATE_OPTIONS(values.country)
                                  : [],
                              })}
                              {...(field.name === 'country' && {
                                options: COUNTRY_OPTIONS,
                                onChange: () => {
                                  form.batch(() => {
                                    form.change('state', null);
                                  });
                                },
                              })}
                            />
                          );
                        })}
                      </Row>
                      <AutosaveEffect
                        data={values}
                        onSave={handleSubmit}
                        interval={600}
                      />
                      <Flex mt={20} justifyContent="flex-end">
                        <Button
                          id="exit-edit-button"
                          onClick={finishEditing}
                          loading={saving}
                        >
                          Finish Editing
                        </Button>
                      </Flex>
                    </form>
                  )}
                />
              </Flex>
            </>
          ) : (
            <Flex flexDirection="column">
              <Flex justifyContent="space-between">
                <Flex flexDirection="column">
                  <Row style={{ overflowWrap: 'break-word' }}>
                    {GENERAL_INFO_FIELDS.map((field) =>
                      field.name === 'empty' ? (
                        <Column key={field.name} mt={2} pb={3} width={300} />
                      ) : (
                        <Column
                          key={field.name}
                          mt={2}
                          pb={3}
                          width={field.width || 300}
                        >
                          <FieldText data={data} {...field} />
                        </Column>
                      ),
                    )}
                  </Row>
                </Flex>
                {logo && (
                  <Box>
                    <Image src={logo.path} alt={`${data.name} Logo`} />
                  </Box>
                )}
              </Flex>
              <Flex mt={20} justifyContent="flex-end">
                {canEdit ? (
                  <Button
                    id="enter-edit-button"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                ) : (
                  ``
                )}
              </Flex>
            </Flex>
          )}
        </Card.Body>
      </ExpiredCard>
    </>
  );
};

export default GeneralInformation;
